import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { MdOutlineBrush } from 'react-icons/md';
import SafeIcon from '../SafeIcon';
import TopNav from '../builder/TopNav';
import DashboardOverview from './store/DashboardOverview';
import ProductsPanel from './store/ProductsPanel';
import OrdersPanel from './store/OrdersPanel';
import StoreSettingsPanel from './store/StoreSettingsPanel';

const {
  FiGrid, FiShoppingBag, FiBox, FiPercent,
  FiSettings, FiMoon, FiSun,
} = FiIcons;

const ACCENT = '#c8f542';

const StoreSidebar = ({ activeTab, setActiveTab, onClose, isDark, setIsDark }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FiGrid },
    { id: 'orders', label: 'Orders', icon: FiShoppingBag },
    { id: 'products', label: 'Products', icon: FiBox },
    { id: 'discounts', label: 'Discounts', icon: FiPercent },
    { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  const bg = isDark ? 'bg-[#0f172a]' : 'bg-white';
  const border = isDark ? 'border-[#334155]' : 'border-gray-200';
  const textTitle = isDark ? 'text-white' : 'text-gray-800';

  return (
    <div className={`w-56 flex flex-col h-full shrink-0 shadow-[2px_0_10px_rgba(0,0,0,0.03)] border-r transition-colors ${bg} ${border}`}>
      <div className={`px-4 py-5 border-b shrink-0 ${border}`}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">E-Commerce</p>
        <h2 className={`text-base font-bold ${textTitle}`}>Store Dashboard</h2>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 custom-scrollbar">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`group w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-medium transition-all relative ${
                isActive
                  ? 'text-[#c8f542] bg-[#c8f542]/10'
                  : isDark
                    ? 'text-gray-400 hover:text-[#c8f542] hover:bg-[#c8f542]/10'
                    : 'text-gray-500 hover:text-[#c8f542] hover:bg-[#c8f542]/10'
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-[#c8f542] rounded-r-full" />
              )}
              <SafeIcon
                icon={item.icon}
                className={`w-4 h-4 shrink-0 transition-colors ${
                  isActive ? 'text-[#c8f542]' : 'text-gray-400 group-hover:text-[#c8f542]'
                }`}
              />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className={`p-4 border-t shrink-0 space-y-4 ${border}`}>
        <div
          className="flex items-center justify-between px-2 cursor-pointer"
          onClick={() => setIsDark(!isDark)}
        >
          <div className="flex items-center space-x-2">
            <SafeIcon
              icon={isDark ? FiMoon : FiSun}
              className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            />
            <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Dark Mode
            </span>
          </div>
          <button
            type="button"
            className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${isDark ? 'bg-[#c8f542]' : 'bg-gray-300'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 shadow-sm ${
                isDark ? 'translate-x-4 bg-black' : 'translate-x-0 bg-white'
              }`}
            />
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-full py-2.5 text-black font-bold rounded-xl text-xs uppercase tracking-widest transition-colors shadow-sm flex items-center justify-center space-x-2 hover:bg-[#d4ff50]"
          style={{ backgroundColor: ACCENT }}
        >
          <SafeIcon icon={MdOutlineBrush} className="w-4 h-4" />
          <span>Back to Builder</span>
        </button>
      </div>
    </div>
  );
};

const ComingSoonPanel = ({ title, isDark }) => (
  <div className={`absolute inset-0 flex items-center justify-center transition-colors ${isDark ? 'bg-[#0f172a]' : 'bg-[#f0f2f5]'}`}>
    <div className="text-center">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border ${isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'}`}>
        <SafeIcon icon={FiSettings} className={`w-7 h-7 ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />
      </div>
      <h3 className={`text-base font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{title}</h3>
      <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Coming soon</p>
    </div>
  </div>
);

const EcommerceDashboard = ({
  onClose, menuStyles, onMenuStylesChange,
  projectId, activeWebsiteName, onOpenSites, onStoreStylesChange,
}) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isDark, setIsDark] = useState(true);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardOverview projectId={projectId} isDark={isDark} />;
      case 'orders': return <OrdersPanel projectId={projectId} isDark={isDark} />;
      case 'products': return <ProductsPanel projectId={projectId} isDark={isDark} />;
      case 'settings': return <StoreSettingsPanel projectId={projectId} isDark={isDark} />;
      default: return <ComingSoonPanel title={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} isDark={isDark} />;
    }
  };

  return (
    <div className={`flex flex-col h-screen w-full font-sans overflow-hidden transition-colors ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
      <TopNav
        view="store"
        activeWebsiteName={activeWebsiteName}
        onOpenSites={onOpenSites}
        onOpenBuilder={onClose}
        onOpenStore={() => {}}
        menuStyles={menuStyles}
        onMenuStylesChange={onMenuStylesChange}
        onUndo={() => {}}
        onRedo={() => {}}
        canUndo={false}
        canRedo={false}
      />
      <div className="flex flex-1 overflow-hidden min-h-0">
        <StoreSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onClose={onClose}
          isDark={isDark}
          setIsDark={setIsDark}
        />
        <div className={`flex-1 relative overflow-hidden transition-colors ${isDark ? 'bg-[#0f172a]' : 'bg-[#f0f2f5]'}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default EcommerceDashboard;